import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useState } from "react";
import { clientAPI } from "../api/client";
import { NumberOfPerPage } from "../utils/globalVariable";
import { formatDateHourDb } from "../utils/tools";
import { customDateFormatType, formatNumber } from "../utils/utils";

const NUMBER_NFT_PER_PAGE = NumberOfPerPage.brokerDebtCertificateManagement;
const queryKey = "BrokerDebtCertificate";

async function fetchBrokerDebtCertificate(
  currentPage,
  search,
  employeeId,
  startTime,
  endTime,
) {
  try {
    const options = {
      limit: NUMBER_NFT_PER_PAGE,
      page: currentPage,
    };

    console.log({ options });

    if (search !== undefined) options.search = search;
    if (employeeId) options.employeeId = employeeId;
    if (startTime) options.startTime = customDateFormatType(startTime);
    if (endTime) options.endTime = customDateFormatType(endTime);

    const queryParams = new URLSearchParams(options).toString();
    let data = await clientAPI("get", `/brokerDebtCertificate?${queryParams}`);
    const totalPages = Math.ceil(data?.pagination?.total / NUMBER_NFT_PER_PAGE);
    const entries = data?.pagination?.total;
    const startEntry =
      (data?.pagination?.page - 1) * data?.pagination?.limit + 1;
    const endEntry = Math.min(
      data?.pagination?.page * data?.pagination?.limit,
      entries,
    );

    const newData = data?.items.map((item, index) => {
      // Thuế TNCN: mặc định 10% × Số tiền tạm giữ khi chưa set; admin sửa được.
      // Chỉ để hiển thị/lưu trữ, không tham gia logic tính toán.
      const taxTNCNValue =
        item?.taxTNCN != null
          ? Number(item.taxTNCN)
          : 0.1 * Number(item?.amountHeld || 0);
      // Số tiền còn lại (tầng hiển thị) = tạm giữ - thuế - đã trả. Chỉ tính ở
      // tầng hiển thị cho đúng thuế; KHÔNG ghi đè giá trị lưu trong DB.
      const remaining =
        Number(item?.amountHeld || 0) -
        taxTNCNValue -
        Number(item?.amountPaid || 0);
      return {
        id: item?._id || "",
        codeEmployee: item?.employee?.code || "-",
        nameEmployee: item?.employee?.fullName || "-",
        amountHeld: item?.amountHeld
          ? `${formatNumber(Math.round(Number(item?.amountHeld)))} VNĐ`
          : "0 VNĐ",
        taxTNCN: `${formatNumber(Math.round(taxTNCNValue))} VNĐ`,
        amountPaid: item?.amountPaid
          ? `${formatNumber(Math.round(Number(item?.amountPaid)))} VNĐ`
          : "0 VNĐ",
        // `|| 0` để -0 (do làm tròn số lẻ của thuế, vd -0.3) hiển thị thành 0
        amountActualRemaining: `${formatNumber(Math.round(remaining) || 0)} VNĐ`,
        createdAt: item?.createdAt ? formatDateHourDb(item?.createdAt) : "-",
        updatedAt: item?.updatedAt ? formatDateHourDb(item?.updatedAt) : "-",
        employeeId: item?.employeeId,
        isBrokerDebtCertificate:
          item?.employee?.isBrokerDebtCertificate || false,
        edit: {
          id: item?._id || "",
          employeeId: item?.employeeId || "",
          amountHeld: item?.amountHeld || 0,
          amountActualRemaining: item?.amountActualRemaining || 0,
          amountPaid: item?.amountPaid || 0,
          // Prefill form Edit: giá trị đã lưu, hoặc mặc định 10% × còn lại.
          taxTNCN: Math.round(taxTNCNValue),
        },
      };
    });

    return { data: newData, totalPages, entries, startEntry, endEntry };
  } catch (error) {
    console.error("Error fetching useBrokerDebtCertificate data:", error);
    return { data: [], totalPages: 0 };
  }
}

export function useBrokerDebtCertificate(
  search,
  employeeId,
  startDate,
  endDate,
) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isRefetching } = useQuery({
    queryKey: [queryKey, currentPage, search, employeeId, startDate, endDate],
    queryFn: () =>
      fetchBrokerDebtCertificate(
        currentPage,
        search,
        employeeId,
        startDate,
        endDate,
      ),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    placeholderData: keepPreviousData,
    onError: (error) => {
      console.error("Error fetching checkIn data:", error);
    },
  });

  const nextPage = () => {
    if (currentPage < data?.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return {
    brokerDebtCertificateData: data?.data,
    totalPages: data?.totalPages,
    refetch,
    isLoading,
    isRefetching,
    prevPage,
    nextPage,
    setCurrentPage,
    currentPage,
    entries: data?.entries,
    startEntry: data?.startEntry,
    endEntry: data?.endEntry,
  };
}
